'use client';

import { useEffect, useRef } from 'react';

import { Box } from '@highlight-cp/core-ui';

import { createHalftoneLoader } from './halftone-loader';
import classes from './pulse-loader.module.css';

export interface PulseLoaderProps {
  /** Edge length of the loader in px (default 28, the logo's native size). */
  size?: number;
  /**
   * Accessible name for the loader. Omitted by default, which hides the loader from assistive tech
   * because it almost always sits beside its own status text; pass one for a standalone loader.
   */
  label?: string;
}

/**
 * ### PulseLoader
 *
 * @description
 * The animated standalone Pulse mark: the halftone logo breaks apart into a spinning sphere, then
 * a volume waveform, then reassembles. Honors `prefers-reduced-motion` by holding the static logo.
 *
 * @example
 * <PulseLoader />
 * <PulseLoader size={44} label="Finding matching Twins" />
 *
 * @param props - {@link PulseLoaderProps}
 * @returns React component
 */
export const PulseLoader = ({ size = 28, label }: PulseLoaderProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const loader = createHalftoneLoader(canvas);
    return () => loader.destroy();
  }, []);

  const accessibleName =
    label === undefined
      ? { 'aria-hidden': true as const }
      : { role: 'img' as const, 'aria-label': label };

  return (
    <Box className={classes.frame} h={size} w={size} {...accessibleName}>
      <canvas ref={canvasRef} className={classes.canvas} />
    </Box>
  );
};
